import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, FormHelperText, IconButton, Typography } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Undo as UndoIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useEditProfileMutation } from '../../features/api/rest/apiProfileSlice';
import { setAlertMessage } from '../../features/layoutSlice';
import ProfileAvatar from '../ProfileAvatar';

function EditButton({ setMode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <IconButton variant="solid" size="small" color="neutral" onClick={() => setMode()}>
        <EditIcon />
      </IconButton>
    </Box>
  );
}

EditButton.propTypes = {
  setMode: PropTypes.func.isRequired,
};

function ProfilePicture({ picture = null }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isFileReady, setIsFileReady] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const [editProfileMutationTrigger] = useEditProfileMutation();

  const submitProfilePicture = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('profile_picture', fileInputRef.current.files[0]);
      await editProfileMutationTrigger(formData).unwrap();
      URL.revokeObjectURL(fileInputRef.current.files[0]);
      dispatch(setAlertMessage({ text: t('profile.editForm.fields.profilePicture.messages.update.success'), type: 'success' }));
    } catch (error) {
      dispatch(setAlertMessage({ text: t('profile.editForm.fields.profilePicture.messages.update.error'), type: 'error' }));
    }
  };

  const handleFileChange = () => {
    if (fileInputRef.current.files[0].type !== 'image/jpeg' && fileInputRef.current.files[0].type !== 'image/png') {
      dispatch(setAlertMessage({ text: t('profile.editForm.fields.profilePicture.messages.upload.error'), type: 'error' }));
      return;
    }
    setIsFileReady(true);
  };

  const handleUndo = () => {
    fileInputRef.current.value = '';
    URL.revokeObjectURL(fileInputRef.current.files[0]);
    setIsFileReady(false);
  };

  const handleDeleteProfilePicture = async () => {
    try {
      await editProfileMutationTrigger({ profile_picture: '' }).unwrap();
      dispatch(setAlertMessage({ text: t('profile.editForm.fields.profilePicture.messages.delete.success'), type: 'success' }));
    } catch (error) {
      dispatch(setAlertMessage({ text: t('profile.editForm.fields.profilePicture.messages.delete.error'), type: 'error' }));
    }
  };

  return (
    <Box data-testid="ProfilePicture" component="form" spacing={2} autoComplete="off" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', alignItems: 'center' }} onSubmit={(e) => submitProfilePicture(e)}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: 150, height: 150 }}>
        <ProfileAvatar size="large" previewPicture={isFileReady ? fileInputRef.current.files[0] : null} />
        <IconButton variant="solid" size="small" color="primary" sx={{ position: 'absolute', top: 0, right: -15 }} disabled={!isFileReady} onClick={handleUndo} data-testid="UndoPreviewPicture">
          <UndoIcon />
        </IconButton>
        <IconButton variant="solid" size="small" color="secondary" sx={{ position: 'absolute', bottom: 0, right: -15 }} onClick={handleDeleteProfilePicture} disabled={!picture} data-testid="DeleteProfilePicture">
          <DeleteIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 3, pl: { sm: 10 }, display: { xs: 'flex', sm: 'block' }, flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="body1">{t('profile.editForm.fields.profilePicture.label')}</Typography>
        <Button
          variant="outlined"
          color="primary"
          component="label"
          htmlFor="ProfilePictureInput"
          sx={{
            width: 150,
            my: 2,
          }}
        >
          {t('profile.editForm.fields.profilePicture.actions.upload')}
          <input type="file" id="ProfilePictureInput" name="profilePicture" accept=".jpg,.jpeg,.png" hidden onChange={handleFileChange} ref={fileInputRef} data-testid="ProfilePictureInput" />
        </Button>
        <FormHelperText>{t('profile.editForm.fields.profilePicture.helperText')}</FormHelperText>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button type="submit" variant="contained" color="primary" disabled={!isFileReady}>
          {t('profile.editForm.fields.profilePicture.actions.save')}
        </Button>
      </Box>
    </Box>
  );
}

ProfilePicture.propTypes = {
  picture: PropTypes.string,
};

export default ProfilePicture;
